import React from "react";
import { Document, Page, View, Text, Image, StyleSheet } from "@react-pdf/renderer";
import { COMPANY_HEADER } from "../lib/company";

// PDF Note de crédit (avoir) — même charte que InvoicePdf, sans QR-Bill.
const fmtCHF = (n) => `${(Number(n) || 0).toFixed(2)}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-CH", { day: "2-digit", month: "long", year: "numeric" }); } catch { return iso; }
};
const origin = typeof window !== "undefined" ? window.location.origin : "";

export const REASON_LABEL = {
  RETOUR_MARCHANDISE: "Retour marchandise",
  ERREUR_FACTURATION: "Erreur de facturation",
  GESTE_COMMERCIAL: "Geste commercial",
  ANNULATION_COMMANDE: "Annulation de commande",
  INVENDUS_DLC: "Invendus — DLC dépassée",
  AUTRE: "Autre",
};

const s = StyleSheet.create({
  page: { padding: 36, fontFamily: "Helvetica", fontSize: 9, color: "#1A1C1A" },
  headerRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 },
  senderBlock: { flexDirection: "row", alignItems: "flex-start", gap: 10, maxWidth: "58%" },
  senderMeta: { fontSize: 8, color: "#575A57", lineHeight: 1.4 },
  titleRight: { flexDirection: "column", alignItems: "flex-end", maxWidth: "42%" },
  titleMain: { fontSize: 20, fontFamily: "Helvetica-Bold", letterSpacing: 1.2, color: "#8B3A1F" },
  titleRef: { fontSize: 11, fontFamily: "Helvetica-Bold", marginTop: 2 },
  titleDate: { fontSize: 8.5, color: "#575A57", marginTop: 1 },
  debtorWindow: { position: "absolute", top: "48mm", left: "115mm", width: "85mm", height: "45mm", padding: 4 },
  debtorName: { fontSize: 10, fontFamily: "Helvetica-Bold", marginBottom: 1 },
  debtorLine: { fontSize: 9.5, lineHeight: 1.35 },
  refsRow: { flexDirection: "row", gap: 12, marginBottom: 12 },
  refBox: { flex: 1, padding: 8, borderLeft: "3pt solid #C1A063", backgroundColor: "#FAFAF7" },
  refBoxReason: { flex: 1, padding: 8, borderLeft: "3pt solid #8B3A1F", backgroundColor: "#FAFAF7" },
  refLabel: { fontSize: 7, color: "#A3A6A3", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 3 },
  refLine: { fontSize: 9, lineHeight: 1.4 },
  refStrong: { fontSize: 9.5, fontFamily: "Helvetica-Bold" },
  table: { borderTop: "1pt solid #E5E5E0", borderLeft: "1pt solid #E5E5E0", borderRight: "1pt solid #E5E5E0", marginBottom: 6 },
  th: { flexDirection: "row", backgroundColor: "#F4F4F0", padding: 6, borderBottom: "1pt solid #E5E5E0" },
  thCell: { fontFamily: "Helvetica-Bold", fontSize: 8, color: "#575A57", textTransform: "uppercase", letterSpacing: 0.5 },
  tr: { flexDirection: "row", padding: 6, borderBottom: "1pt solid #E5E5E0" },
  totals: { alignSelf: "flex-end", width: "55%", marginTop: 6 },
  totalRow: { flexDirection: "row", justifyContent: "space-between", paddingVertical: 2 },
  totalLabel: { fontSize: 9, color: "#575A57" },
  totalValue: { fontSize: 9, fontFamily: "Helvetica-Bold" },
  totalGrand: { flexDirection: "row", justifyContent: "space-between", paddingTop: 6, marginTop: 4, borderTop: "1.5pt solid #1A1C1A" },
  settleBox: { marginTop: 14, padding: 8, borderLeft: "3pt solid #1F4E3D", backgroundColor: "#F4F7F4" },
  footer: { position: "absolute", bottom: 24, left: 36, right: 36, fontSize: 7, color: "#A3A6A3", textAlign: "center", borderTop: "0.5pt solid #E5E5E0", paddingTop: 6 },
});

const Sender = ({ nc, settings }) => {
  const addr = (nc.creditor_address || settings?.company_address || "").split("\n").filter(Boolean)[0];
  const zip = nc.creditor_postal_code || settings?.company_postal_code || "";
  const city = nc.creditor_city || settings?.company_city || "";
  const phone = nc.creditor_phone || settings?.company_phone || "";
  const vat = nc.creditor_vat_number || settings?.company_vat_number || "";
  return (
    <View style={s.senderBlock}>
      <Image src={settings?.company_logo_url || `${origin}/logo-maisonthai-pdf.png`} style={{ width: 55, height: 55 }} />
      <View>
        <Image src={`${origin}/company-name-sayonara.png`} style={{ width: 149, height: 15, marginBottom: 4 }} />
        {addr ? <Text style={s.senderMeta}>{addr}</Text> : null}
        {zip && city ? <Text style={s.senderMeta}>CH-{zip} {city}</Text> : null}
        {phone ? <Text style={s.senderMeta}>Tél {phone}</Text> : null}
        {vat ? <Text style={s.senderMeta}>TVA : {vat}</Text> : null}
      </View>
    </View>
  );
};

const Debtor = ({ nc }) => {
  const isCH = !nc.debtor_country || nc.debtor_country === "CH";
  return (
    <View style={s.debtorWindow}>
      <Text style={s.debtorName}>{nc.debtor_name || "Client"}</Text>
      {nc.debtor_contact_name ? <Text style={s.debtorLine}>{nc.debtor_contact_name}</Text> : null}
      {nc.debtor_address ? <Text style={s.debtorLine}>{nc.debtor_address}</Text> : null}
      {(nc.debtor_postal_code || nc.debtor_city) ? (
        <Text style={s.debtorLine}>{isCH ? "CH-" : ""}{nc.debtor_postal_code} {nc.debtor_city}</Text>
      ) : null}
    </View>
  );
};

const Lines = ({ lines }) => (
  <View style={s.table}>
    <View style={s.th}>
      <Text style={[s.thCell, { flex: 4 }]}>Désignation</Text>
      <Text style={[s.thCell, { flex: 1.1, textAlign: "right" }]}>Qté / Unité</Text>
      <Text style={[s.thCell, { flex: 1, textAlign: "right" }]}>PU CHF</Text>
      <Text style={[s.thCell, { flex: 0.6, textAlign: "right" }]}>TVA</Text>
      <Text style={[s.thCell, { flex: 1.1, textAlign: "right" }]}>Total CHF</Text>
    </View>
    {lines.map((ln, i) => (
      <View key={ln.id || i} style={s.tr}>
        <Text style={{ flex: 4 }}>{ln.description}</Text>
        <Text style={{ flex: 1.1, textAlign: "right" }}>{ln.quantity}{ln.unit ? ` ${ln.unit}` : ""}</Text>
        <Text style={{ flex: 1, textAlign: "right" }}>{fmtCHF(ln.unit_price)}</Text>
        <Text style={{ flex: 0.6, textAlign: "right" }}>{ln.vat_rate}%</Text>
        <Text style={{ flex: 1.1, textAlign: "right", fontFamily: "Helvetica-Bold" }}>{fmtCHF(ln.quantity * ln.unit_price)}</Text>
      </View>
    ))}
  </View>
);

const Totals = ({ nc }) => (
  <View style={s.totals}>
    <View style={s.totalRow}><Text style={s.totalLabel}>Total HT</Text><Text style={s.totalValue}>{fmtCHF(nc.total_ht)} CHF</Text></View>
    <View style={s.totalRow}><Text style={s.totalLabel}>TVA</Text><Text style={s.totalValue}>{fmtCHF(nc.total_vat)} CHF</Text></View>
    {Math.abs(Number(nc.rounding_adjustment || 0)) >= 0.005 ? (
      <View style={s.totalRow}><Text style={s.totalLabel}>Arrondi 0.05 (CH)</Text><Text style={s.totalValue}>{fmtCHF(nc.rounding_adjustment)} CHF</Text></View>
    ) : null}
    <View style={s.totalGrand}>
      <Text style={{ fontSize: 11, fontFamily: "Helvetica-Bold" }}>TOTAL AVOIR TTC</Text>
      <Text style={{ fontSize: 12, fontFamily: "Helvetica-Bold", color: "#8B3A1F" }}>-{fmtCHF(nc.total_ttc)} CHF</Text>
    </View>
  </View>
);

const Settlement = ({ nc }) => {
  const apps = nc.applications || [];
  const refunded = Number(nc.refunded_amount || 0);
  const remaining = Number(nc.total_ttc || 0) - Number(nc.applied_total || 0) - refunded;
  return (
    <View style={s.settleBox}>
      <Text style={s.refLabel}>Règlement de l&apos;avoir</Text>
      {apps.map((a, i) => (
        <Text key={i} style={s.refLine}>Imputé sur la facture <Text style={s.refStrong}>{a.invoice_reference}</Text> : {fmtCHF(a.amount)} CHF</Text>
      ))}
      {refunded > 0 ? (
        <Text style={s.refLine}>Remboursé le {fmtDate(nc.refunded_at)} : <Text style={s.refStrong}>{fmtCHF(refunded)} CHF</Text></Text>
      ) : null}
      {remaining > 0.004 ? (
        <Text style={s.refLine}>Solde en votre faveur : <Text style={s.refStrong}>{fmtCHF(remaining)} CHF</Text>, déductible de vos prochaines factures.</Text>
      ) : null}
    </View>
  );
};

export default function CreditNotePdf({ nc, settings }) {
  return (
    <Document>
      <Page size="A4" style={s.page}>
        <View style={s.headerRow}>
          <Sender nc={nc} settings={settings} />
          <View style={s.titleRight}>
            <Text style={s.titleMain}>NOTE DE CRÉDIT</Text>
            <Text style={s.titleRef}>N° {nc.reference}</Text>
            <Text style={s.titleDate}>Émise le {fmtDate(nc.issue_date)}</Text>
          </View>
        </View>
        <Debtor nc={nc} />
        <View style={{ height: "50mm" }} />
        <View style={s.refsRow}>
          <View style={s.refBox}>
            <Text style={s.refLabel}>Références</Text>
            {nc.source_invoice_reference ? <Text style={s.refLine}>Facture d&apos;origine : <Text style={s.refStrong}>{nc.source_invoice_reference}</Text></Text> : null}
            {nc.source_bl_reference ? <Text style={s.refLine}>Bon de livraison : <Text style={s.refStrong}>{nc.source_bl_reference}</Text></Text> : null}
            {!nc.source_invoice_reference && !nc.source_bl_reference ? <Text style={s.refLine}>—</Text> : null}
          </View>
          <View style={s.refBoxReason}>
            <Text style={s.refLabel}>Motif</Text>
            <Text style={s.refStrong}>{REASON_LABEL[nc.reason] || nc.reason}</Text>
            {nc.reason_detail ? <Text style={s.refLine}>{nc.reason_detail}</Text> : null}
          </View>
        </View>
        <Lines lines={nc.lines || []} />
        <Totals nc={nc} />
        <Settlement nc={nc} />
        {nc.additional_info ? <Text style={{ marginTop: 10, color: "#575A57", lineHeight: 1.4 }}>{nc.additional_info}</Text> : null}
        <Text style={s.footer}>Note de crédit {nc.reference} émise par {COMPANY_HEADER} le {fmtDate(nc.issue_date)}</Text>
      </Page>
    </Document>
  );
}
